import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  Button,
  Box,
  Typography,
  DialogActions,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  Divider,
  Card,
  CardContent,
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { axiosInstance } from "../../utils/axios";
import styled from "@emotion/styled";

const TrackContainer = styled(Box)`
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #ddd;
  align-items: center;
`;

const TrackDetails = styled(Box)`
  text-align: left;
`;

const TrackActions = styled(Box)`
  display: flex;
  align-items: center;
`;

const ColorPreview = styled(Box)(({ color }) => ({
  width: 24,
  height: 24,
  borderRadius: "50%",
  backgroundColor: color || "#1976d2",
  marginRight: 10,
  display: "inline-block",
  verticalAlign: "middle",
}));

const TrackTab = ({ eventId: propEventId, onTrackAdded, onClose }) => {
  const params = useParams();
  const eventId =
    propEventId ||
    params.eventId ||
    window.location.pathname.split("/view-event/")[1];
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [editTrack, setEditTrack] = useState(null);

  useEffect(() => {
    if (eventId) {
      fetchTracks();
    }
  }, [eventId]);

  const fetchTracks = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post("/graphql", {
        query: `
          query Tracks($eventId: ID!) {
            tracks(eventId: $eventId) {
              id
              name
              description
              color
            }
          }
        `,
        variables: {
          eventId: eventId,
        },
      });

      if (response.errors) {
        throw new Error(response.errors[0]?.message || "Error fetching tracks");
      }

      const fetchedTracks = response.data?.tracks || [];
      setTracks(fetchedTracks);
    } catch (error) {
      console.error("Error fetching tracks", error);
      setAlert({
        open: true,
        message: error.message || "Failed to fetch tracks",
        severity: "error",
      });
      setTracks([]);
    } finally {
      setLoading(false);
    }
  };

  const validationSchema = Yup.object({
    trackName: Yup.string()
      .max(50, "Track name cannot be longer than 50 characters")
      .required("Track name is required"),
    description: Yup.string().max(
      200,
      "Description cannot be longer than 200 characters"
    ),
    color: Yup.string().matches(
      /^#([0-9A-F]{3}){1,2}$/i,
      "Must be a valid hex color code"
    ),
  });

  const formik = useFormik({
    initialValues: {
      trackName: "",
      description: "",
      color: "#1976d2",
    },
    validationSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        setSubmitting(true);

        if (editTrack) {
          // Update existing track
          const response = await axiosInstance.post("/graphql", {
            query: `
              mutation UpdateTrack($id: ID!, $eventId: ID!, $input: UpdateTrackInput!) {
                updateTrack(id: $id, eventId: $eventId, input: $input) {
                  id
                  name
                  description
                  color
                }
              }
            `,
            variables: {
              id: editTrack.id,
              eventId: eventId,
              input: {
                name: values.trackName,
                description: values.description,
                color: values.color,
              },
            },
          });

          if (response.errors) {
            throw new Error(
              response.errors[0]?.message || "Error updating track"
            );
          }

          setAlert({
            open: true,
            message: "Track updated successfully!",
            severity: "success",
          });

          setEditTrack(null);
        } else {
          // Create new track
          const response = await axiosInstance.post("/graphql", {
            query: `
              mutation AddTrack($input: AddTrackInput!) {
                addTrack(input: $input) {
                  id
                  name
                  description
                  color
                }
              }
            `,
            variables: {
              input: {
                name: values.trackName,
                description: values.description,
                color: values.color,
                eventId: eventId,
              },
            },
          });

          if (response.errors) {
            throw new Error(
              response.errors[0]?.message || "Error creating track"
            );
          }

          setAlert({
            open: true,
            message: "Track created successfully!",
            severity: "success",
          });
        }

        resetForm();
        await fetchTracks();

        if (typeof onTrackAdded === "function") {
          onTrackAdded();
        }
      } catch (error) {
        console.error("Error saving track:", error);
        setAlert({
          open: true,
          message: error.message || "Failed to save track",
          severity: "error",
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleEditTrack = (track) => {
    setEditTrack(track);
    formik.setValues({
      trackName: track.name,
      description: track.description || "",
      color: track.color || "#1976d2",
    });
  };

  const handleDeleteTrack = async (trackId) => {
    const trackToDelete = tracks.find((track) => track.id === trackId);
    const trackName = trackToDelete?.name || "this track";

    // Show confirmation dialog
    if (
      !window.confirm(
        `Are you sure you want to delete "${trackName}"?\n\nThis action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.post("/graphql", {
        query: `
          mutation DeleteTrack($id: ID!, $eventId: ID!) {
            deleteTrack(id: $id, eventId: $eventId)
          }
        `,
        variables: {
          id: trackId,
          eventId: eventId,
        },
      });

      console.log("🗑️ Full response:", response);
      console.log("🗑️ Response.data:", response.data);
      console.log("🗑️ Response.data.data:", response.data?.data);

      if (response.data?.errors) {
        // Handle GraphQL errors with specific messages
        const errorMessage =
          response.data.errors[0]?.message || "Error deleting track";
        throw new Error(errorMessage);
      }

      // Check multiple possible response structures
      let deleteResult;

      if (response.data?.data?.deleteTrack !== undefined) {
        // Standard GraphQL response structure
        deleteResult = response.data.data.deleteTrack;
      } else if (response.data?.deleteTrack !== undefined) {
        // Direct response structure
        deleteResult = response.data.deleteTrack;
      } else {
        console.log("🗑️ Could not find deleteTrack in response structure");
        throw new Error("Unexpected response format from server");
      }

      console.log(
        "🗑️ Delete result value:",
        deleteResult,
        "Type:",
        typeof deleteResult
      );

      if (deleteResult === true) {
        setAlert({
          open: true,
          message: `Track "${trackName}" deleted successfully!`,
          severity: "success",
        });
        await fetchTracks();
      } else {
        console.log(
          "🗑️ Delete operation returned unexpected result:",
          deleteResult
        );
        throw new Error(
          `Failed to delete track - server returned: ${deleteResult}`
        );
      }
    } catch (error) {
      console.error("Error deleting track:", error);

      // Check if it's a dependency error and show a more user-friendly message
      let errorMessage = error.message || "Failed to delete track";
      if (
        errorMessage.includes("used by") &&
        (errorMessage.includes("session") || errorMessage.includes("break"))
      ) {
        errorMessage = `${errorMessage}\n\nPlease remove the track from these sessions/breaks first, then try deleting again.`;
      }

      setAlert({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditTrack(null);
    formik.resetForm();
    formik.setValues({
      trackName: "",
      description: "",
      color: "#1976d2",
    });
    formik.setTouched({});
    formik.setErrors({});

    // Close the drawer if onClose function is provided
    if (typeof onClose === "function") {
      onClose();
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
        >
          {alert.message}
        </Alert>
      </Snackbar>

      {/* Form Section */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h5" gutterBottom>
                  {editTrack ? "Edit Track" : "Add Track"}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="trackName"
                  name="trackName"
                  label="Track Name"
                  value={formik.values.trackName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.trackName && Boolean(formik.errors.trackName)
                  }
                  helperText={
                    formik.touched.trackName && formik.errors.trackName
                  }
                  variant="outlined"
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="description"
                  name="description"
                  label="Description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.description &&
                    Boolean(formik.errors.description)
                  }
                  helperText={
                    formik.touched.description && formik.errors.description
                  }
                  multiline
                  rows={2}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" alignItems="center">
                  <TextField
                    id="color"
                    name="color"
                    label="Color"
                    type="color"
                    value={formik.values.color}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.color && Boolean(formik.errors.color)}
                    helperText={formik.touched.color && formik.errors.color}
                    sx={{
                      width: "100px",
                      "& input": {
                        height: "40px",
                        padding: "5px",
                      },
                    }}
                  />
                  <Typography variant="body2" sx={{ ml: 2 }}>
                    {formik.values.color}
                  </Typography>
                  <Box sx={{ ml: 2, flexGrow: 1 }}>
                    <Box
                      sx={{
                        height: "20px",
                        backgroundColor: formik.values.color,
                        borderRadius: "4px",
                        width: "100%",
                      }}
                    />
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end" gap={2}>
                  <Button
                    variant="outlined"
                    onClick={handleCancelEdit}
                    disabled={formik.isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={formik.isSubmitting || !formik.isValid}
                  >
                    {formik.isSubmitting ? (
                      <CircularProgress size={24} />
                    ) : editTrack ? (
                      "Update Track"
                    ) : (
                      "Add Track"
                    )}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* Tracks List Section */}
      <Box>
        <Typography variant="h6" gutterBottom>
          Existing Tracks
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" my={3}>
            <CircularProgress />
          </Box>
        ) : tracks.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body2" color="textSecondary">
              No tracks available yet. Add your first track above.
            </Typography>
          </Paper>
        ) : (
          <Paper sx={{ p: 2 }}>
            {tracks.map((track) => (
              <TrackContainer key={track.id}>
                <TrackDetails>
                  <Box display="flex" alignItems="center">
                    <ColorPreview color={track.color} />
                    <Typography variant="h6">{track.name}</Typography>
                  </Box>
                  {track.description && (
                    <Typography variant="body2" color="textSecondary">
                      {track.description}
                    </Typography>
                  )}
                </TrackDetails>
                <TrackActions>
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={() => handleEditTrack(track)}
                    disabled={loading}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => handleDeleteTrack(track.id)}
                    disabled={loading}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TrackActions>
              </TrackContainer>
            ))}
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default TrackTab;
